import React, { useContext, useState, useEffect, useCallback } from 'react';
import ProcessingStatus from '../components/processingStatus';
import useBreakpoint from '../utils/useBreakpoint';
import ScrollTop from '../components/scrollTop';
import { Container } from './Container';
import Customer from '../stores/Customer';
import WidgetConfigContext from './WidgetConfigContext';
import PurchaseSubscription from './PurchaseSubscription';
import ChangePaymentDetails from './ChangePaymentDetails';
import ErrorBoundary from './ErrorBoundary';
import useResize from '../utils/useResize';
import {
	VatUpdateError,
	BillingDetailsUpdateError,
	MissingCustomerError,
	SumUpError,
	PurchaseSubscriptionError,
	ChangePaymentDetailsError,
	PaymentMethodVerificationError,
	ApiError, ErrorPaymentMethodType
} from '../utils/errors';
import { VIEW_TYPES, PAYMENT_METHODS } from '../constants';
import { TOKENIZATION_PAYMENT_METHODS } from '../constants/paymentMethod';
import { MESSAGE_TYPES, TRACING_EVENTS } from '../../shared/constants';
import { notifyParent, trace } from '../utils/notifyParent';
import crossWindow from '../utils/crossWindow';

const ERROR_PAYMENT_METHOD_TYPES = {
	[PAYMENT_METHODS.creditCard]: ErrorPaymentMethodType.card,
	[PAYMENT_METHODS.suPayout]: ErrorPaymentMethodType.su_payouts,
	[PAYMENT_METHODS.suPaymentMethods]: ErrorPaymentMethodType.su_payment_methods,
	[PAYMENT_METHODS.suBusinessAccount]: ErrorPaymentMethodType.su_business_account,
};

const toErrorPaymentMethodType = (paymentMethodType) =>
	ERROR_PAYMENT_METHOD_TYPES[paymentMethodType] || ErrorPaymentMethodType.direct_debit;

const Widget = (props) => {
	const ctx = useContext(WidgetConfigContext);
	const { getConfig } = ctx;
	const [ processingState, setProcessingState ] = useState();
	const [ error, setError ] = useState();
	const [ customer, setCustomer ] = useState();
	const [ initiated, setInitiated ] = useState(false);
	const isMobile = !useBreakpoint();
	const config = getConfig.result;

	const {
		type,
		showCardZipCode,
		showBillingDetails,
		showVatNumber,
		planId,
		billingDetails,
		legalInfo,
		businessAccount,
		availablePaymentMethods,
		legalEntity,
		showCoupon,
		couponCode,
		showEditMode,
		redirectUrl,
		onError, onSuccess,
		widgetOffset,
		customerReference,
		promotionId,
		discountId,
	} = props;

	const notifyWidgetLoaded = useCallback(() => setTimeout(() => {
		notifyParent({ message: MESSAGE_TYPES.ON_LOAD });
	}, 1));

	useResize(({ width, height }) => {
		notifyParent({
			message: MESSAGE_TYPES.ON_RESIZE,
			payload: { width, height },
		});
	});

	useEffect(() => {
		// handle loading config errors
		if (!initiated) {
			if (getConfig.status === 'error') {
				const { error } = getConfig;
				setProcessingState({ error });
				setError(true);
				setInitiated(true);
				notifyWidgetLoaded();
				onError(error.serialize());
				error.propagate();
			}
		}

		if (!config || initiated) {
			return;
		}

		const customerStore = new Customer({
			sumupService: config.sumupService,
			epayService: config.epayService,
			sumupSubscriptionsService: config.sumupSubscriptionsService,
			merchantWalletService: config.merchantWalletService,
		});
		const checkCustomerExists = () => {
			if (!customerStore.billingDetails) {
				throw new MissingCustomerError();
			}
		};

		const handleInitiationError = (error) => {
			setProcessingState({ error });
			setError(true);
			notifyWidgetLoaded();
		};

		customerStore.fetchCustomerDetails()
			.then(async () => {
				if (type === VIEW_TYPES.purchase) {
					if (!customerStore.billingDetails) {
						customerStore.setBillingDetails(billingDetails);
					}
					customerStore.setCouponCode({ couponCode, planId });
				} else {
					// we must fail if the billing details does not exist for change payment details
					// as we only pass billing details for purchase
					checkCustomerExists();
				}

				// Load MW PMs availability before the first render, so the PaymentMethod component
				// picks its default from merged embedder + MW availability.
				await customerStore.initializePaymentMethodAvailability(availablePaymentMethods, {
					countryCode: customerStore.billingDetails?.country,
				});
			})
			.then(notifyWidgetLoaded)
			.catch(handleInitiationError)
			.finally(() => {
				customerStore.setCustomerReference(customerReference);
				// Customer fetch can fail before MW init; keep tokenized-only embedder flags either way.
				customerStore.mergeEmbedderPaymentMethodAvailability(availablePaymentMethods);
				setCustomer(customerStore);
				setInitiated(true);
			});

	});

	// Widget embedder flags can update after init.
	// Re-merge into cached MW PMs availability without another GET.
	useEffect(() => {
		if (!customer || !initiated) {
			return;
		}

		customer.mergeEmbedderPaymentMethodAvailability(availablePaymentMethods);
	}, [customer, initiated, availablePaymentMethods]);

	useEffect(() => {
		if (customerReference) {
			customer?.setCustomerReference(customerReference);
		}
	}, [customerReference]);

	const do3DSChallenge = ({ url, method, payload }) => {
		const challengeData = { type: '3ds', url, method, payload };

		return new Promise((resolve, reject) => {
			notifyParent({ message: MESSAGE_TYPES.CARD_AUTHENTICATION_START, payload: { challengeData } });
			const removeListener = crossWindow.addMessageListener(MESSAGE_TYPES.CARD_AUTHENTICATION_FINISH, (msg) => {
				const { error, checkoutId } = msg;

				removeListener();
				error ? reject(error) : resolve(checkoutId);
			});
		});
	};

	const complete3DSChallenge = async (checkout) => {
		let checkoutId;

		if (checkout.next_step) {
			checkoutId = await do3DSChallenge(checkout.next_step);
			trace(TRACING_EVENTS.SCA_VERIFICATION_FINISHED, { flowType: 'two-step' });
		} else if (checkout.id) {
			checkoutId = checkout.id;
			trace(TRACING_EVENTS.SCA_VERIFICATION_FINISHED, { flowType: 'frictionless' });
		} else {
			new SumUpError('Unexpected response from SumUp api, missing checkoutId, next_steps payload')
				.addContext({ checkout })
				.propagate()
				.throw();
		}

		return checkoutId;
	};

	const purchaseSubscription = async (callback) => {
		trace(TRACING_EVENTS.PURCHASE_ATTEMPTED, { paymentMethodType: customer.paymentMethodType });
		const requiresTokenization = TOKENIZATION_PAYMENT_METHODS.includes(customer.paymentMethodType);

		try {
			let paymentMethodData;
			if (customer.isPersistedPaymentMethodAvailable) {
				paymentMethodData = await customer.getPersistedPaymentMethodForPurchase();
			} else {
				const prepareProcessResult = await customer.prepareSubscriptionPurchase({
					redirectUrl,
				});

				if (requiresTokenization) {
					const checkoutId = await complete3DSChallenge(prepareProcessResult);
					paymentMethodData = await customer.verifyPaymentMethod({ checkoutId });
				} else {
					paymentMethodData = prepareProcessResult;
				}
			}

			const subscriptionPurchaseDetails = await customer
				.purchaseSubscription({ planId, paymentMethodData, promotionId, discountId });

			callback && callback(subscriptionPurchaseDetails);

			return {};
		} catch (error) {
			if (error instanceof PaymentMethodVerificationError || error instanceof ApiError) {
				throw error;
			}
			const paymentMethodType = toErrorPaymentMethodType(customer.paymentMethodType);
			throw new PurchaseSubscriptionError(paymentMethodType, error.message);
		}
	};

	const changePaymentDetails = async (callback) => {
		const requiresTokenization = TOKENIZATION_PAYMENT_METHODS.includes(customer.paymentMethodType);

		try {
			const prepareUpdateResult = await customer.preparePaymentMethodUpdate({
				redirectUrl,
			});
			let paymentMethodData;

			let checkoutId;
			if (requiresTokenization) {
				checkoutId = await complete3DSChallenge(prepareUpdateResult);

				paymentMethodData = await customer.verifyPaymentMethod({ checkoutId });
			} else {
				paymentMethodData = prepareUpdateResult;
			}

			const paymentMethodUpdateResult = await customer.updatePaymentMethod(paymentMethodData);

			callback && callback(paymentMethodUpdateResult);

			const { paymentMethodType } = paymentMethodUpdateResult;
			if (paymentMethodType === PAYMENT_METHODS.creditCard) {
				return { creditCardUpdated: paymentMethodUpdateResult };
			} else if (paymentMethodType === PAYMENT_METHODS.directDebit) {
				return { directDebitUpdated: paymentMethodUpdateResult };
			}

			return { deductionFromPayoutsUpdated: paymentMethodUpdateResult };
		} catch (error) {
			if (error instanceof PaymentMethodVerificationError || error instanceof ApiError) {
				throw error;
			}
			const paymentMethodType = toErrorPaymentMethodType(customer.paymentMethodType);
			throw new ChangePaymentDetailsError(paymentMethodType, error.message);
		}
	};

	const changeVatNumber = async (callback, formValues = {}) => {
		if (Object.prototype.hasOwnProperty.call(formValues, 'vatNumber')) {
			customer.setVatNumber(formValues.vatNumber);
		}

		let vatNumber;
		try {
			vatNumber = await customer.updateVatNumber();
		} catch (e) {
			throw new VatUpdateError();
		}

		callback && callback({ vatNumber });
		return {
			vatNumberUpdated: { vatNumber },
		};
	};

	const changeBillingDetails = async (callback) => {
		let billingDetails;
		try {
			billingDetails = await customer.updateBillingDetails();
		} catch (e) {
			throw new BillingDetailsUpdateError();
		}

		callback && callback({ billingDetails });
		return {
			billingDetailsUpdated: { billingDetails },
		};
	};

	const action = (handler, callback) => async (formValues) => {
		let error;
		let success;

		try {
			success = await handler(callback, formValues);
		} catch (e) {
			e.propagate && e.propagate();
			error = e;
		}

		setProcessingState({ error, success });

		return { error, success };
	};

	// loading state
	if (!initiated) {
		return null;
	}

	// TODO: remove hack to prevent rendering if config or customer is not loaded
	// ideally we should show an error
	if ((!config || !customer) && !error) {
		return null;
	}

	return (
		<Container data-testid="widget-container">
			{processingState && (
				<ProcessingStatus
					dismiss={() => setProcessingState(null)}
					{...processingState}
				/>
			)}
			<ErrorBoundary onError={(error) => setProcessingState({ error })}>
				{!error && <>
					{type === VIEW_TYPES.purchase && <PurchaseSubscription
						planId={planId}
						config={config}
						customer={customer}
						legalInfo={legalInfo}
						showCardZipCode={showCardZipCode}
						showBillingDetails={showBillingDetails}
						showVatNumber={showVatNumber}
						legalEntity={legalEntity}
						showCoupon={showCoupon}
						widgetOffset={widgetOffset}
						isMobile={isMobile}
						businessAccount={businessAccount}
						onSubmit={action(purchaseSubscription, onSuccess)}
						onPaymentDetailsSubmit={action(changePaymentDetails)}
						onVatNumberSubmit={action(changeVatNumber)}
						onBillingDetailsSubmit={action(changeBillingDetails)}
						promotionId={promotionId}
						discountId={discountId}
					/>}
					{type === VIEW_TYPES.changeInfo && <ChangePaymentDetails
						customer={customer}
						config={config}
						showEditMode={showEditMode}
						showCardZipCode={showCardZipCode}
						showBillingDetails={showBillingDetails}
						showVatNumber={showVatNumber}
						legalEntity={legalEntity}
						onPaymentDetailsSubmit={action(changePaymentDetails, onSuccess)}
						onVatNumberSubmit={action(changeVatNumber, onSuccess)}
						onBillingDetailsSubmit={action(changeBillingDetails, onSuccess)}
						isMobile={isMobile}
						businessAccount={businessAccount}
					/>}
				</>}
			</ErrorBoundary>
			<ScrollTop
				widgetOffset={widgetOffset}
				isMobile={isMobile}
			/>
		</Container>
	);
};

export default Widget;
